import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import OnboardingTour from './OnboardingTour'
import {
  LayoutDashboard, Briefcase, Users, Building2,
  Settings, LogOut, ChevronLeft, ChevronRight, TrendingUp, Bell, Menu, CalendarDays, ClipboardList, BarChart2, Sun, Moon, CreditCard, Megaphone, Tag, MessageCircle, NotebookPen,
  User, Search, Star, AlertTriangle, FileText, CheckCircle, XCircle, Mail, RotateCcw, RefreshCw, Trash2, Clock, X, Palette, Wallet
} from 'lucide-react'
import SmartSearch from './SmartSearch'
import api from '../api/axios'
import ChatWidget from './ChatWidget'
import SupportWidget from './SupportWidget'

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/jobs', icon: Briefcase, label: 'Jobs', permission: 'access_jobs' },
  { to: '/staff', icon: Users, label: 'Staff', permission: 'manage_staff', hideForRoles: [] },
  { to: '/clients', icon: Building2, label: 'Clients', permission: 'manage_clients' },
  { to: '/performance', icon: TrendingUp, label: 'Performance', hideForClient: true },
  { to: '/leave', icon: ClipboardList, label: 'Leave', hideForRoles: ['client'] },
  { to: '/attendance', icon: CalendarDays, label: 'Attendance', hideForClient: true },
  { to: '/job-reports', icon: BarChart2, label: 'Job Reports', permission: 'view_job_reports', altPermission: 'view_reports_dashboard' },
  { to: '/client-reports', icon: Building2, label: 'Client Reports', permission: 'view_client_reports' },
  { to: '/revenue', icon: TrendingUp, label: 'Revenue', hideForRoles: ['artist', 'selfqc', 'qc', 'hr', 'tl', 'client'] },
  { to: '/finance', icon: Wallet, label: 'Finance', permission: 'manage_expenses', altPermission: 'manage_invoices' },
  { to: '/inkdeals', icon: Tag, label: 'InkDeals', permission: 'manage_inkdeals' },
  { to: '/announcements', icon: Megaphone, label: 'Announcements', hideForClient: true },
  { to: '/onboarding', icon: ClipboardList, label: 'Onboarding', permission: 'manage_onboarding' },
  { to: '/support', icon: MessageCircle, label: 'Client Support', permission: 'handle_client_support' },
  { to: '/settings', icon: Settings, label: 'Settings', permission: 'manage_system_settings' },
  { to: '/my-reports', icon: BarChart2, label: 'My Reports', showForRoles: ['client'] },
  { to: '/billing', icon: CreditCard, label: 'Billing', showForRoles: ['client'] },
  { to: '/client-settings', icon: Settings, label: 'Settings', showForRoles: ['client'] },
  { to: '/color-reference', icon: Palette, label: 'Color Guide', showForRoles: ['client'] },
]

function PageWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  return (
    <div
      key={location.pathname}
      style={{ animation: 'pageFadeIn 300ms ease forwards' }}
    >
      {children}
    </div>
  )
}

export default function Layout() {
  const { user, logout, hasPermission } = useAuth()
  const navigate = useNavigate()
  const [clockClients, setClockClients] = useState<any[]>([])
  const [currentTime, setCurrentTime] = useState(new Date())
  const { isDark, mode, cycleTheme } = useTheme()
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [showSmartSearch, setShowSmartSearch] = useState(false)
  const [showTour, setShowTour] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showSplash, setShowSplash] = useState(() => {
    const should = sessionStorage.getItem('show_splash') === 'true'
    if (should) sessionStorage.removeItem('show_splash')
    return should
  })

  useEffect(() => {
    if (showSplash) {
      const t = setTimeout(() => setShowSplash(false), 1800)
      return () => clearTimeout(t)
    }
  }, [showSplash])

  // Keyboard shortcuts
  useEffect(() => {
    if (!user || user.roleName === 'client') return

    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      // Allow Cmd+K even from input fields
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        if (hasPermission('smart_search')) setShowSmartSearch(v => !v)
        return
      }
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return
      if ((e.target as HTMLElement)?.isContentEditable) return

      if (e.key === '?') { setShowShortcuts(v => !v); return }

      // Esc — close any open modal
      if (e.key === 'Escape') { setShowShortcuts(false); return }

      // N — new job (only on jobs page)
      if (e.key.toLowerCase() === 'n' && window.location.pathname === '/jobs') {
        window.dispatchEvent(new CustomEvent('shortcut:newjob'))
        return
      }

      // R — refresh (only on jobs page)
      if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey && window.location.pathname === '/jobs') {
        window.dispatchEvent(new CustomEvent('shortcut:refresh'))
        return
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [user])
  const [announcementUnread, setAnnouncementUnread] = useState(0)

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false) }, [])

  useEffect(() => {
    if (user?.userId && user?.onboardingCompleted === false) {
      const t = setTimeout(() => setShowTour(true), 1500)
      return () => clearTimeout(t)
    }
  }, [user?.userId])

  useEffect(() => {
    api.get('/clients/timezones').then(res => {
      // Group clients by timezone
      const tzMap: Record<string, string[]> = {}
      for (const c of (res.data || [])) {
        if (!tzMap[c.timezone]) tzMap[c.timezone] = []
        tzMap[c.timezone].push(c.abbreviation || c.clientCode)
      }
      // Convert to array for display
      const grouped = Object.entries(tzMap).map(([timezone, abbrs]) => ({
        timezone,
        label: abbrs.join('/')
      }))
      setClockClients(grouped)
    }).catch(() => {})
    const tick = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(tick)
  }, [])

  useEffect(() => {
  if (user && user.roleName !== 'client') {
    const fetchAnnouncementUnread = () => {
      api.get('/announcements/unread-count').then((res: any) => setAnnouncementUnread(res.data.count)).catch(() => {})
    }
    fetchAnnouncementUnread()
    const interval = setInterval(fetchAnnouncementUnread, 30000)
    return () => clearInterval(interval)
  }
}, [user])

  return (
    <div className="flex h-screen bg-[#f5f7fa] dark:bg-[#0a1628]">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      {/* Sidebar — desktop floating island */}
      <aside
        className="hidden lg:flex flex-col fixed left-3 top-16 bottom-16 z-30 transition-all duration-300 ease-in-out"
        style={{ width: collapsed ? '60px' : '220px' }}
        onMouseEnter={() => setCollapsed(false)}
        onMouseLeave={() => setCollapsed(true)}
      >
        <div className="flex flex-col h-full bg-navy-900 dark:bg-[#0b1829] rounded-2xl overflow-hidden border border-transparent dark:border-[#1e3450]" style={{ boxShadow: 'inset -2px 0 0 #F46B20' }}>

          {/* Nav items */}
          <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
            {navItems.map(({ to, icon: Icon, label, permission, altPermission, exact, hideForClient, hideForRoles, showForRoles }: any) => {
              if (permission && !hasPermission(permission) && !(altPermission && hasPermission(altPermission))) return null
              if (hideForClient && user?.roleName === 'client') return null
              if (hideForRoles && hideForRoles.includes(user?.roleName)) return null
              if (showForRoles && !showForRoles.includes(user?.roleName)) return null
              return (
                <NavLink key={to} to={to} end={exact}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                      isActive ? 'bg-orange-500 text-white shadow-sm' : 'text-navy-400 dark:text-slate-400 hover:text-white hover:bg-navy-800 dark:hover:bg-white/10'
                    }`
                  }>
                  <Icon size={17} className="flex-shrink-0" />
                  <span className={`flex-1 flex items-center justify-between transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
                    {label}
                    {to === '/announcements' && announcementUnread > 0 && (
                      <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {announcementUnread > 99 ? '99+' : announcementUnread}
                      </span>
                    )}
                  </span>
                </NavLink>
              )
            })}
          </nav>

          {/* User + Sign out */}
          <div className="px-2 py-3 border-t border-navy-800 space-y-0.5">
            {!collapsed && (
              <div className="flex items-center gap-3 px-3 py-2 overflow-hidden">
                <div className="w-7 h-7 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-400 font-bold text-xs">{user?.name?.[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold truncate">{user?.name}</p>
                  <p className="text-navy-400 text-[10px] truncate">{user?.roleLabel}</p>
                </div>
              </div>
            )}
            <button onClick={logout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all whitespace-nowrap">
              <LogOut size={17} className="flex-shrink-0" />
              <span className={`transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>Sign out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Sidebar — mobile drawer */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-[260px] bg-navy-900 dark:bg-[#0b1829] flex flex-col
        transform transition-transform duration-300 lg:hidden
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between px-4 py-5 border-b border-navy-800">
          <NavLink to="/dashboard" onClick={() => setMobileOpen(false)}>
            <img src="/White_Color_Logo.svg" alt="Inkcredible" className="h-7 object-contain" />
          </NavLink>
          <button onClick={() => setMobileOpen(false)} className="text-navy-400 hover:text-white p-1">
            <ChevronLeft size={20} />
          </button>
        </div>

        <nav className="flex-1 py-5 px-3 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:rounded-full">
          {navItems.map(({ to, icon: Icon, label, permission, altPermission, exact, hideForClient, hideForRoles, showForRoles }: any) => {
            if (permission && !hasPermission(permission) && !(altPermission && hasPermission(altPermission))) return null
            if (hideForClient && user?.roleName === 'client') return null
            if (hideForRoles && hideForRoles.includes(user?.roleName)) return null
            if (showForRoles && !showForRoles.includes(user?.roleName)) return null
            return (
              <NavLink key={to} to={to} end={exact} onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                      isActive ? 'bg-orange-500 text-white shadow-orange' : 'text-navy-400 dark:text-slate-400 hover:text-white hover:bg-navy-800 dark:hover:bg-white/10'
                    }`
                  }>
                <Icon size={17} className="flex-shrink-0" />
                  <span className="flex-1">{label}</span>
                  {to === '/announcements' && announcementUnread > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {announcementUnread > 99 ? '99+' : announcementUnread}
                    </span>
                  )}
              </NavLink>
            )
          })}
        </nav>

        <div className="p-3 border-t border-navy-800">
          <div className="flex items-center gap-3 px-2 py-2 mb-1">
            <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <span className="text-orange-400 font-bold text-sm">{user?.name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{user?.name}</p>
              <p className="text-navy-400 text-xs truncate">{user?.roleLabel}</p>
            </div>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-all">
            <LogOut size={15} /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto min-w-0 flex flex-col bg-gray-50 dark:bg-[#060f1e]">
        {/* Top bar */}
        <div className="flex-shrink-0 h-12 bg-white dark:bg-[#080f1d] border-b border-gray-100 dark:border-[#1e3450] flex items-center justify-between px-4 gap-3">
          {/* Hamburger — mobile only */}
          <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-navy-700 hover:bg-gray-100 transition-colors">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1 lg:hidden" />
          <div className="hidden lg:flex items-center flex-1">
            <NavLink to="/dashboard" title="Go to Dashboard" className="opacity-90 hover:opacity-100 transition-opacity">
              <img src="/Full_Color_Logo.svg" alt="Inkcredible" className="h-7 object-contain dark:hidden" />
              <img src="/White_Color_Logo.svg" alt="Inkcredible" className="h-7 object-contain hidden dark:block" />
            </NavLink>
          </div>

          {/* Live clocks — desktop only, staff only */}
          {clockClients.length > 0 && user?.roleName !== 'client' && (
            <div className="hidden lg:flex items-center gap-0 mr-3">
              {clockClients.map((c: any, i: number) => (
                <div key={c.timezone} className={`flex flex-col items-center px-3 ${i < clockClients.length - 1 ? 'border-r border-gray-100 dark:border-navy-700/30' : ''}`}>
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide leading-none mb-0.5">{c.label}</span>
                  <span className="text-xs font-mono font-semibold text-gray-600 dark:text-gray-200 leading-none">
                    {(() => { try { return currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: c.timezone }) } catch { return '--:--' } })()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {hasPermission('smart_search') && (
            <button onClick={() => setShowSmartSearch(true)}
              title="Smart Search (Ctrl+K)"
              className="p-2 rounded-xl text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors mr-1">
              <Search size={18} />
            </button>
          )}

          <button onClick={cycleTheme}
            title={mode === 'light' ? 'Light mode' : mode === 'dark' ? 'Dark mode' : 'Auto mode'}
            className="p-2 rounded-xl text-gray-400 hover:text-navy-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors mr-1 relative">
            {mode === 'light' ? <Moon size={18} /> : mode === 'dark' ? <Sun size={18} /> : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            )}
            {mode === 'auto' && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-400 rounded-full" />
            )}
          </button>
          <NotificationBell />
          <NotePad />
        </div>
        <div className="flex-1 overflow-auto pb-16 lg:pb-0 relative">
          {/* Subtle watermark */}
          <img src="/Orange_Logo_Icon.svg" alt="" className="fixed bottom-0 right-0 w-80 h-80 opacity-[0.08] pointer-events-none select-none dark:opacity-[0.10] hidden lg:block" style={{ transform: 'translate(20%, 20%)' }} />
          <PageWrapper>
            <Outlet />
          </PageWrapper>
        </div>
      </main>

      {showSplash && (
        <div className={`fixed inset-0 z-[99999] bg-white dark:bg-[#0a1628] flex items-center justify-center transition-opacity duration-500 pointer-events-none`}
          style={{ animation: 'fadeOut 0.8s cubic-bezier(0.4, 0, 1, 1) 1.2s forwards' }}>
          <img src="/Orange_Logo_Icon.svg" alt="Inkcredible" className="w-48 h-48"
            style={{ animation: 'smoothPulse 0.8s ease-in-out infinite alternate' }} />
        </div>
      )}

      {showSmartSearch && <SmartSearch onClose={() => setShowSmartSearch(false)} />}

      {showShortcuts && (
        <div className="fixed inset-0 z-[99998] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowShortcuts(false)}>
          <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-navy-900 dark:text-white">Keyboard Shortcuts</h3>
              <button onClick={() => setShowShortcuts(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Actions</p>
                <div className="space-y-2">
                  {[
                    ['Ctrl+K', 'Smart Search'],
                    ['N', 'New job (on Jobs page)'],
                    ['R', 'Refresh jobs (on Jobs page)'],
                    ['Esc', 'Close modal'],
                    ['?', 'Toggle this cheatsheet'],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-navy-700 dark:text-gray-200">{label}</span>
                      <kbd className="px-2 py-0.5 bg-gray-100 dark:bg-[#112240] text-navy-700 dark:text-gray-200 rounded-lg text-xs font-mono border border-gray-200 dark:border-[#1e3a5f]">{key}</kbd>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 mt-5 text-center">Shortcuts are disabled when typing in a field</p>
          </div>
        </div>
      )}

      {showTour && user && (
        <OnboardingTour
          role={user.roleName}
          onComplete={async () => {
            try { await api.post('/auth/onboarding-complete') } catch {}
            setShowTour(false)
          }}
        />
      )}

      {/* Bottom nav — mobile only */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 flex lg:hidden">
        {navItems.filter(({ permission, altPermission, hideForClient, hideForRoles, showForRoles }: any) => {
          if (permission && !hasPermission(permission) && !(altPermission && hasPermission(altPermission))) return false
          if (hideForClient && user?.roleName === 'client') return false
          if (hideForRoles && hideForRoles.includes(user?.roleName)) return false
          if (showForRoles && !showForRoles.includes(user?.roleName)) return false
          return true
        }).filter(({ to }: any) => to !== '/announcements').slice(0, 5).map(({ to, icon: Icon, label, exact }: any) => (
          <NavLink key={to} to={to} end={exact}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center py-2 text-xs font-medium transition-colors ${
                isActive ? 'text-orange-500' : 'text-gray-400'
              }`
            }>
            <Icon size={20} className="mb-0.5" />
            <span className="text-[10px]">{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

function NotificationBell() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<any[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [toasts, setToasts] = useState<any[]>([])
  const ref = useRef<HTMLDivElement>(null)
  const seenIds = useRef<Set<string>>(new Set())
  const isFirstLoad = useRef(true)

  const playNotificationSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const times = [0, 0.15, 0.3]
      times.forEach(time => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.setValueAtTime(880, ctx.currentTime + time)
        osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + time + 0.1)
        gain.gain.setValueAtTime(0.3, ctx.currentTime + time)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + time + 0.12)
        osc.start(ctx.currentTime + time)
        osc.stop(ctx.currentTime + time + 0.15)
      })
    } catch {}
  }

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications?limit=20')
      const prefsRes = await api.get('/notifications/preferences').catch(() => ({ data: {} }))
      const prefs = prefsRes.data
      const fetched = res.data.notifications
      setNotifications(fetched)
      setUnreadCount(res.data.unreadCount)

      if (isFirstLoad.current) {
        // On first load, just mark all current IDs as seen — don't toast
        fetched.forEach((n: any) => seenIds.current.add(n.id))
        isFirstLoad.current = false
        return
      }

      // Check for new toast-worthy notifications
      const prefMap: Record<string, string> = {
        'job_allocated': 'job_allocated',
        'job_created': 'job_created',
        'job_revision': 'job_revision',
        'job_update': 'job_allocated',
        'leave_approved': 'leave_approved',
        'leave_rejected': 'leave_approved',
        'leave_pending': 'leave_applied',
        'new_client_subscribed': 'new_client_subscribed',
        'client_renewed': 'client_renewed',
      }

      const newToastable = fetched.filter((n: any) => {
        if (seenIds.current.has(n.id)) return false
        const prefKey = prefMap[n.type]
        if (prefKey && prefs[prefKey] === false) return false
        return true
      })

      newToastable.forEach((n: any) => {
        seenIds.current.add(n.id)
        const toastId = `toast-${n.id}`
        setToasts(prev => [...prev, { ...n, toastId }])
        playNotificationSound()
        setTimeout(() => {
          setToasts(prev => prev.filter(t => t.toastId !== toastId))
        }, 10000)
      })
    } catch {}
  }

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 10000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const markAllRead = async () => {
    await api.patch('/notifications/read-all')
    fetchNotifications()
  }

  const markRead = async (id: string) => {
    await api.patch(`/notifications/${id}/read`)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
    setUnreadCount(prev => Math.max(0, prev - 1))
  }

  const clearAll = async () => {
    await api.delete('/notifications/clear-all')
    fetchNotifications()
  }

const typeIcon: Record<string, React.ReactNode> = {
    job_created: <Mail className="w-5 h-5 text-indigo-400" />,
    job_allocated: <User className="w-5 h-5 text-blue-400" />,
    job_submitted_qc: <Search className="w-5 h-5 text-purple-400" />,
    job_correction: <RotateCcw className="w-5 h-5 text-red-400" />,
    job_revision: <RefreshCw className="w-5 h-5 text-blue-400" />,
    job_update: <Star className="w-5 h-5 text-yellow-400" />,
    subscription_low: <AlertTriangle className="w-5 h-5 text-orange-400" />,
    leave_applied: <FileText className="w-5 h-5 text-gray-400" />,
    leave_approved: <CheckCircle className="w-5 h-5 text-green-400" />,
    leave_rejected: <XCircle className="w-5 h-5 text-red-400" />
  }

  const dismissToast = (toastId: string) => {
    setToasts(prev => prev.filter(t => t.toastId !== toastId))
  }

  return (
    <div ref={ref} className="relative">
      {/* Toast notifications */}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.toastId}
            className="pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-2xl shadow-2xl w-80 animate-fade-in"
            style={{
              backgroundColor: toast.type === 'leave_approved' || toast.type === 'job_update'
                ? 'rgba(22, 101, 52, 0.75)'
                : toast.type === 'leave_rejected'
                ? 'rgba(153, 27, 27, 0.75)'
                : 'rgba(120, 40, 0, 0.75)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${toast.type === 'leave_approved' || toast.type === 'job_update' ? 'rgba(34, 197, 94, 0.9)' : toast.type === 'leave_rejected' ? 'rgba(239, 68, 68, 0.9)' : 'rgba(180, 60, 0, 0.9)'}`
            }}>
            <User className="w-5 h-5 text-white flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">{toast.title}</p>
              {toast.message && <p className="text-orange-100 text-xs mt-0.5">{toast.message}</p>}
              {toast.jobCode && <p className="text-white/80 text-xs font-mono mt-1">{toast.jobCode}</p>}
            </div>
            <button
              onClick={() => dismissToast(toast.toastId)}
              className="text-white/70 hover:text-white text-lg leading-none flex-shrink-0 mt-0.5 transition-colors">
              ×
            </button>
          </div>
        ))}
      </div>
      <button onClick={() => { setOpen(!open); if (!open) fetchNotifications() }}
        className="relative p-2 rounded-xl text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors">
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-[90] bg-black/20 backdrop-blur-sm" onClick={() => setOpen(false)} />
      )}

      {/* Slide-in panel */}
      <div className={`fixed top-0 right-0 h-full w-96 bg-white dark:bg-[#0d1e35] shadow-2xl z-[91] flex flex-col transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-orange-500" />
            <p className="font-display font-bold text-navy-900 dark:text-white">Notifications</p>
            {unreadCount > 0 && (
              <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-orange-500 hover:text-orange-700 font-medium">Mark all read</button>
            )}
            <button onClick={clearAll} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1a3050]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        {/* Notifications list */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="flex justify-center mb-3"><Bell className="w-10 h-10 text-gray-300" /></div>
              <p className="font-semibold text-navy-900 dark:text-white mb-1">All caught up!</p>
              <p className="text-sm text-gray-400">No notifications yet</p>
            </div>
          ) : (
            <div>
              {(() => {
                const now = new Date()
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                const yesterday = new Date(today.getTime() - 86400000)
                const thisWeek = new Date(today.getTime() - 7 * 86400000)

                const groups: { label: string, items: any[] }[] = [
                  { label: 'Today', items: [] },
                  { label: 'Yesterday', items: [] },
                  { label: 'This Week', items: [] },
                  { label: 'Older', items: [] }
                ]

                notifications.forEach(n => {
                  const d = new Date(n.createdAt)
                  if (d >= today) groups[0].items.push(n)
                  else if (d >= yesterday) groups[1].items.push(n)
                  else if (d >= thisWeek) groups[2].items.push(n)
                  else groups[3].items.push(n)
                })

                return groups.filter(g => g.items.length > 0).map(group => (
                  <div key={group.label}>
                    <div className="px-5 py-2 bg-gray-50 dark:bg-[#0a1628] sticky top-0">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{group.label}</p>
                    </div>
                    <div className="divide-y divide-gray-50 dark:divide-[#1a3050]">
                      {group.items.map(n => (
                        <div key={n.id}
                          onClick={async () => {
                            if (!n.isRead) await markRead(n.id)
                            if (n.jobId) { setOpen(false); navigate(`/jobs?openJob=${n.jobId}`) }
                          }}
                          className={`px-5 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors ${!n.isRead ? 'bg-orange-50/40 dark:bg-orange-900/5' : ''}`}>
                          <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 mt-0.5">{typeIcon[n.type] || <Bell className="w-5 h-5 text-gray-400" />}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-navy-800 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>{n.title}</p>
                              {n.message && <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>}
                              {n.jobCode && <p className="text-xs font-mono text-orange-400 mt-0.5">{n.jobCode}</p>}
                              <p className="text-xs text-gray-300 dark:text-gray-600 mt-1.5">
                                {new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            {!n.isRead && <div className="w-2 h-2 bg-orange-400 rounded-full flex-shrink-0 mt-2" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              })()}
            </div>
          )}
        </div>
      </div>
      <ChatWidget />
      <SupportWidget />
    </div>
  )
}

const NOTE_COLORS: Record<string, { bg: string, border: string, label: string }> = {
  default: { bg: 'bg-white dark:bg-[#0d1e35]', border: 'border-gray-100 dark:border-[#1e3a5f]', label: 'Default' },
  yellow:  { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-500/30', label: 'Yellow' },
  green:   { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-500/30', label: 'Green' },
  blue:    { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-500/30', label: 'Blue' },
  pink:    { bg: 'bg-pink-50 dark:bg-pink-900/20', border: 'border-pink-200 dark:border-pink-500/30', label: 'Pink' },
  purple:  { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-500/30', label: 'Purple' },
}

function NotePad() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState<any[]>([])
  const [selectedNote, setSelectedNote] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [showReminder, setShowReminder] = useState(false)
  const [staffList, setStaffList] = useState<any[]>([])
  const [selectedStaff, setSelectedStaff] = useState<string[]>([])
  const [sharing, setSharing] = useState(false)
  const [shareSearch, setShareSearch] = useState('')
  const saveTimer = useRef<any>(null)

  const fetchNotes = async () => {
    const res = await api.get('/notes')
    setNotes(res.data)
  }

  useEffect(() => {
    if (user && user.roleName !== 'client' && open) {
      fetchNotes()
    }
  }, [user, open])

  const handleCreate = async () => {
    try {
      const res = await api.post('/notes', { noteType: 'note', color: 'default' })
      setNotes(prev => [res.data, ...prev])
      setSelectedNote(res.data)
    } catch {}
  }

  const handleChange = (field: string, val: string) => {
    setSelectedNote((prev: any) => ({ ...prev, [field]: val }))
    setNotes(prev => prev.map((n: any) => n.id === selectedNote.id ? { ...n, [field]: val } : n))
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      setSaving(true)
      try {
        await api.put(`/notes/${selectedNote.id}`, { [field]: val })
      } catch {}
      setSaving(false)
    }, 1000)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this note?')) return
    await api.delete(`/notes/${id}`)
    const updated = notes.filter((n: any) => n.id !== id)
    setNotes(updated)
    setSelectedNote(updated.length > 0 ? updated[0] : null)
  }

  const handleRevokeShare = async (shareUserId: string) => {
    try {
      await api.delete(`/notes/${selectedNote.id}/share/${shareUserId}`)
      const updatedSharedWith = selectedNote.sharedWith.filter((s: any) => s.id !== shareUserId)
      setSelectedNote((prev: any) => ({ ...prev, sharedWith: updatedSharedWith }))
      setNotes(prev => prev.map((n: any) => n.id === selectedNote.id ? { ...n, sharedWith: updatedSharedWith } : n))
    } catch (e: any) {
      alert('Failed to revoke access')
    }
  }

  if (!user || user.roleName === 'client') return null

  const ownNotes = notes.filter((n: any) => n.isOwner)
  const sharedNotes = notes.filter((n: any) => !n.isOwner)

  return (
    <>
      <button onClick={() => setOpen(v => !v)}
        title="My Notepad"
        className={`p-2 rounded-xl transition-colors mr-1 ${open ? 'text-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'text-gray-400 hover:text-navy-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
        <NotebookPen size={18} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Split drawer — wider to accommodate split view */}
          <div className="fixed top-0 right-0 h-full w-full sm:w-[600px] z-50 bg-white dark:bg-[#0d1e35] shadow-2xl border-l border-t border-b border-gray-100 dark:border-[#1e3a5f] flex flex-col sm:rounded-l-2xl">

            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <NotebookPen size={15} className="text-orange-500" />
                <p className="font-display font-semibold text-navy-900 dark:text-white text-sm">My Notepad</p>
                {saving && <span className="text-xs text-orange-400 animate-pulse">Saving...</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleCreate} title="New Note"
                  className="flex items-center gap-1 text-xs bg-orange-500 hover:bg-orange-600 text-white px-2.5 py-1.5 rounded-lg font-medium transition-colors">
                  + New
                </button>
                <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"><X className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Split content */}
            <div className="flex flex-1 overflow-hidden">

              {/* Left — Notes list */}
              <div className="w-32 sm:w-48 flex-shrink-0 border-r border-gray-100 dark:border-[#1e3a5f] overflow-y-auto flex flex-col">
                {notes.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4 text-center py-8">
                    <NotebookPen size={24} className="text-gray-200 dark:text-gray-700" />
                    <p className="text-xs text-gray-400">No notes yet</p>
                    <button onClick={handleCreate}
                      className="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg">
                      Create one
                    </button>
                  </div>
                ) : (
                  <>
                    {ownNotes.length > 0 && (
                      <div>
                        <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-[#0a1929]">My Notes</p>
                        {ownNotes.map((n: any) => (
                          <div key={n.id} onClick={() => { setSelectedNote(n); setShowShare(false); setShowReminder(false); setSelectedStaff([]); setShareSearch('') }}
                            className={`px-3 py-2.5 cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] transition-all relative
                              ${selectedNote?.id === n.id ? 'bg-orange-50 dark:bg-orange-900/20' : 'hover:bg-gray-50 dark:hover:bg-[#1a3050]'}
                            `}>
                            {/* Color stripe */}
                            {n.color && n.color !== 'default' && (
                              <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${
                                n.color === 'yellow' ? 'bg-yellow-400' :
                                n.color === 'green' ? 'bg-green-400' :
                                n.color === 'blue' ? 'bg-blue-400' :
                                n.color === 'pink' ? 'bg-pink-400' : 'bg-purple-400'
                              }`} />
                            )}
                            <div className="flex items-start justify-between gap-1">
                              <p className="text-xs font-semibold text-navy-800 dark:text-white truncate flex-1">{n.title || 'Untitled'}</p>
                              <div className="flex gap-0.5 flex-shrink-0">
                                {n.sharedWith?.length > 0 && <span className="text-xs" title="Shared">👥</span>}
                                {n.reminderAt && !n.reminderSent && <span title="Reminder"><Bell className="w-3 h-3 text-orange-500 flex-shrink-0" /></span>}
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 truncate mt-0.5">{n.content?.slice(0, 35) || 'Empty'}</p>
                            <p className="text-xs text-gray-300 dark:text-gray-600 mt-0.5">
                              {new Date(n.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    {sharedNotes.length > 0 && (
                      <div>
                        <p className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-[#0a1929]">Shared with me</p>
                        {sharedNotes.map((n: any) => (
                          <div key={n.id} onClick={() => { setSelectedNote(n); setShowShare(false); setShowReminder(false); setSelectedStaff([]); setShareSearch('') }}
                            className={`px-3 py-2.5 cursor-pointer border-b border-gray-50 dark:border-[#1e3a5f] transition-all relative
                              ${selectedNote?.id === n.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-[#1a3050]'}
                            `}>
                            {n.color && n.color !== 'default' && (
                              <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${
                                n.color === 'yellow' ? 'bg-yellow-400' :
                                n.color === 'green' ? 'bg-green-400' :
                                n.color === 'blue' ? 'bg-blue-400' :
                                n.color === 'pink' ? 'bg-pink-400' : 'bg-purple-400'
                              }`} />
                            )}
                            <p className="text-xs font-semibold text-navy-800 dark:text-white truncate">{n.title || 'Untitled'}</p>
                            <p className="text-xs text-gray-400 truncate mt-0.5">{n.content?.slice(0, 35) || 'Empty'}</p>
                            <p className="text-xs text-blue-400 mt-0.5">Shared</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Right — Editor */}
              {selectedNote ? (
                <div className={`flex-1 flex flex-col overflow-hidden ${NOTE_COLORS[selectedNote.color || 'default'].bg}`}>

                  {/* Editor toolbar */}
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center gap-2 flex-wrap flex-shrink-0">
                    {/* Color picker */}
                    <div className="flex gap-1 items-center">
                      {Object.entries(NOTE_COLORS).map(([key, val]) => (
                        <button key={key} title={`Color: ${val.label}`}
                          onClick={() => {
                            api.put(`/notes/${selectedNote.id}`, { color: key })
                            setSelectedNote((prev: any) => ({ ...prev, color: key }))
                            setNotes(prev => prev.map((n: any) => n.id === selectedNote.id ? { ...n, color: key } : n))
                          }}
                          className={`w-3.5 h-3.5 rounded-full border transition-transform hover:scale-125 ${
                            key === 'default' ? 'bg-white border-gray-300' :
                            key === 'yellow' ? 'bg-yellow-300 border-yellow-400' :
                            key === 'green' ? 'bg-green-300 border-green-400' :
                            key === 'blue' ? 'bg-blue-300 border-blue-400' :
                            key === 'pink' ? 'bg-pink-300 border-pink-400' : 'bg-purple-300 border-purple-400'
                          } ${selectedNote.color === key ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''}`} />
                      ))}
                    </div>

                    <div className="flex-1" />

                    {/* Reminder button */}
                    <button title="Set Reminder" onClick={() => { setShowReminder(v => !v); setShowShare(false) }}
                      className={`text-xs px-2 py-1 rounded-lg transition-colors flex items-center gap-1 ${showReminder ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600' : 'text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'}`}>
                      <Bell className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Remind</span>
                    </button>

                    {/* Share button — only for owner */}
                    {selectedNote.isOwner && (
                      <button title="Share Note" onClick={async () => {
                        if (!showShare) {
                          const res = await api.get('/staff/list-all')
                          setStaffList(res.data.filter((s: any) => s.id !== user?.userId?.toString()))
                          setSelectedStaff([])
                        }
                        setShowShare(v => !v)
                        setShowReminder(false)
                      }}
                        className={`text-xs px-2 py-1 rounded-lg transition-colors flex items-center gap-1 ${showShare ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}>
                        👥 <span className="hidden sm:inline">Share</span>
                      </button>
                    )}

                    {/* Delete — only owner */}
                    {selectedNote.isOwner && (
                      <button title="Delete Note" onClick={() => handleDelete(selectedNote.id)}
                        className="text-xs px-2 py-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Reminder panel */}
                  {showReminder && (
                    <div className="px-4 py-3 border-b border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-orange-900/10 space-y-2 flex-shrink-0">
                      <p className="text-xs font-semibold text-orange-600 flex items-center gap-1"><Bell className="w-3 h-3" /> Set Reminder</p>
                      <div className="flex gap-2">
                        <input type="datetime-local" className="input text-xs py-1 flex-1"
                          value={selectedNote.reminderAt ? new Date(selectedNote.reminderAt).toISOString().slice(0,16) : ''}
                          onChange={e => {
                            const val = e.target.value
                            api.put(`/notes/${selectedNote.id}`, { reminderAt: val || null })
                            setSelectedNote((prev: any) => ({ ...prev, reminderAt: val, reminderSent: false }))
                            setNotes(prev => prev.map((n: any) => n.id === selectedNote.id ? { ...n, reminderAt: val } : n))
                          }} />
                        {selectedNote.reminderAt && (
                          <button onClick={() => {
                            api.put(`/notes/${selectedNote.id}`, { reminderAt: null })
                            setSelectedNote((prev: any) => ({ ...prev, reminderAt: null }))
                            setNotes(prev => prev.map((n: any) => n.id === selectedNote.id ? { ...n, reminderAt: null } : n))
                          }} className="text-xs text-red-400 hover:text-red-600">Clear</button>
                        )}
                      </div>
                      {selectedNote.reminderAt && (
                        <p className="text-xs text-orange-500">
                          {selectedNote.reminderSent ? <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" /> Reminder sent</span> : <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(selectedNote.reminderAt).toLocaleString('en-IN')}</span>}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Share panel */}
                  {showShare && selectedNote.isOwner && (
                    <div className="px-4 py-3 border-b border-blue-100 dark:border-blue-500/20 bg-blue-50 dark:bg-blue-900/10 space-y-2 flex-shrink-0">
                      <p className="text-xs font-semibold text-blue-600">👥 Share Note (live collaboration)</p>

                      {/* Already shared with */}
                      {selectedNote.sharedWith?.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500">Currently shared with:</p>
                          {selectedNote.sharedWith.map((s: any) => (
                            <div key={s.id} className="flex items-center justify-between bg-white dark:bg-[#0d1e35] rounded-lg px-2 py-1">
                              <span className="text-xs text-navy-800 dark:text-white">{s.name}</span>
                              <button onClick={() => handleRevokeShare(s.id)}
                                className="text-xs text-red-400 hover:text-red-600">Revoke</button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Search */}
                      <input type="text" placeholder="Search staff..." className="input text-xs py-1 w-full"
                        value={shareSearch} onChange={e => setShareSearch(e.target.value)} />

                      {/* Share with new people */}
                      <div className="max-h-28 overflow-y-auto space-y-0.5">
                        {staffList
                          .filter((s: any) => !selectedNote.sharedWith?.find((sw: any) => sw.id === s.id))
                          .filter((s: any) => s.name.toLowerCase().includes(shareSearch.toLowerCase()))
                          .map((s: any) => (
                          <label key={s.id} className="flex items-center gap-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded px-2 py-1">
                            <input type="checkbox" className="accent-blue-500"
                              checked={selectedStaff.includes(s.id)}
                              onChange={e => {
                                e.stopPropagation()
                                setSelectedStaff(prev => e.target.checked ? [...prev, s.id] : prev.filter(id => id !== s.id))
                              }} />
                            <span className="text-xs text-navy-800 dark:text-white">{s.name}</span>
                            <span className="text-xs text-gray-400 ml-auto capitalize">{s.roleName}</span>
                          </label>
                        ))}
                      </div>
                      {selectedStaff.length > 0 && (
                        <button onClick={async () => {
                          setSharing(true)
                          try {
                            await api.post(`/notes/${selectedNote.id}/share`, { userIds: selectedStaff })
                            const res = await api.get('/notes')
                            setNotes(res.data)
                            const updated = res.data.find((n: any) => n.id === selectedNote.id)
                            if (updated) setSelectedNote(updated)
                            setSelectedStaff([])
                            setShareSearch('')
                          } catch {}
                          setSharing(false)
                        }} disabled={sharing}
                          className="text-xs bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg font-medium w-full">
                          {sharing ? 'Sharing...' : `Share with ${selectedStaff.length} person${selectedStaff.length !== 1 ? 's' : ''}`}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Shared note badge */}
                  {!selectedNote.isOwner && (
                    <div className="px-4 py-1.5 bg-blue-50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-500/20 flex-shrink-0">
                      <p className="text-xs text-blue-500">👥 Shared note — your edits are visible to all collaborators</p>
                    </div>
                  )}

                  {/* Title */}
                  <input
                    className="px-4 py-3 text-sm font-bold text-navy-800 dark:text-white bg-transparent border-b border-gray-100 dark:border-[#1e3a5f] focus:outline-none placeholder-gray-300 dark:placeholder-gray-600 flex-shrink-0"
                    placeholder="Note title..."
                    value={selectedNote.title === 'Untitled' ? '' : selectedNote.title}
                    onChange={e => handleChange('title', e.target.value || 'Untitled')}
                  />

                  {/* Content */}
                  <textarea
                    className="flex-1 px-4 py-3 resize-none bg-transparent text-sm text-navy-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none leading-relaxed"
                    placeholder="Start writing..."
                    value={selectedNote.content}
                    onChange={e => handleChange('content', e.target.value)}
                    autoFocus
                  />

                  {/* Footer */}
                  <div className="px-4 py-2 border-t border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between flex-shrink-0">
                    <p className="text-xs text-gray-400">
                      {selectedNote.content?.length || 0} chars
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(selectedNote.updatedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
                  <NotebookPen size={36} className="text-gray-200 dark:text-gray-700" />
                  <p className="text-sm text-gray-400">Select a note to edit</p>
                  <button onClick={handleCreate}
                    className="text-sm bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl font-medium transition-colors">
                    + Create New Note
                  </button>
                </div>
              )}
            </div>

            {/* Bottom status bar */}
            <div className="px-4 py-2 border-t border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between flex-shrink-0 bg-gray-50 dark:bg-[#0a1929]">
              <p className="text-xs text-gray-400">{notes.length} note{notes.length !== 1 ? 's' : ''} · Private</p>
              {saving && <p className="text-xs text-orange-400 animate-pulse">Saving...</p>}
            </div>
          </div>
        </>
      )}
    </>
  )
}